import { createContext } from 'react';
import type { JSX, Dispatch } from 'react';

import { NULL_FUNCTION } from '@e-socna/empty';
import type { FragmentType } from '@e-socna/data-layer';

import { initialState } from './types/state.type';
import { useNavigationReducer } from './hooks/useReducer.hook';
import type { Page } from './enums/page.enum';
import type { NavigationContextAction } from './types/actions.type';
import type { NavigationState } from './types/state.type';
import type { NavigationContext_FolderSpaceInfo_Folder_Fragment } from './fragments/folder.fragment';
import type { NavigationContext_MissionSpaceInfo_Mission_Fragment } from './fragments/mission.fragment';

interface UiData {
  readonly navigation: NavigationState;
  readonly dispatchNavigation: Dispatch<NavigationContextAction>;
  readonly setPage: (page: Page) => void;
  readonly setFolderSpaceInfo: (
    folder: FragmentType<
      typeof NavigationContext_FolderSpaceInfo_Folder_Fragment
    >,
  ) => void;
  readonly flushFolderSpaceInfo: () => void;
  readonly setMissionSpaceInfo: (
    mission: FragmentType<
      typeof NavigationContext_MissionSpaceInfo_Mission_Fragment
    >,
  ) => void;
  readonly flushMissionSpaceInfo: () => void;
}

const NavigationContext = createContext<UiData>({
  navigation: initialState,
  dispatchNavigation: NULL_FUNCTION,
  setPage: NULL_FUNCTION,
  setFolderSpaceInfo: NULL_FUNCTION,
  flushFolderSpaceInfo: NULL_FUNCTION,
  setMissionSpaceInfo: NULL_FUNCTION,
  flushMissionSpaceInfo: NULL_FUNCTION,
});

interface Props {
  readonly children: JSX.Element;
}

const NavigationContextProvider = ({
  children,
}: Readonly<Props>): JSX.Element => {
  const navigationReducer = useNavigationReducer();

  return (
    <NavigationContext.Provider value={navigationReducer}>
      {children}
    </NavigationContext.Provider>
  );
};

export { NavigationContext, NavigationContextProvider };
